/**
 * Formatters - Format data for UI display
 *
 * Features:
 * - Format prices in CLP (Chilean Peso)
 * - Format dates in Spanish (Chilean locale)
 * - Format phone numbers in Chilean format
 * - Format dimensions with units
 *
 * @module utils/formatters
 *
 * @example
 * import { formatPrice, formatDate, formatPhone } from '@/utils/formatters';
 *
 * formatPrice(1500000) // "$ 1.500.000"
 * formatDate("2025-01-15") // "15 de enero de 2025"
 * formatPhone("+56912345678") // "+56 9 1234 5678"
 */

/**
 * Format price in Chilean Pesos (CLP)
 * @param {number} amount - Price amount
 * @param {Object} [options] - Formatting options
 * @param {boolean} [options.include_currency=true] - Include currency symbol
 * @param {boolean} [options.include_decimals=false] - Include decimal places
 * @returns {string} Formatted price
 *
 * @example
 * formatPrice(1500000)
 * // Returns: "$ 1.500.000"
 *
 * formatPrice(1500.50, { include_decimals: true })
 * // Returns: "$ 1.500,50"
 */
export function formatPrice(amount, options = {}) {
  const {
    include_currency = true,
    include_decimals = false,
  } = options;

  if (amount === null || amount === undefined || isNaN(amount)) {
    return '-';
  }

  const number = Number(amount);

  // Format with Chilean locale (periods for thousands, comma for decimals)
  let formatted;

  if (include_decimals) {
    formatted = number.toFixed(2).replace('.', ',');
  } else {
    formatted = Math.round(number).toString();
  }

  // Add thousands separators (periods)
  const parts = formatted.split(',');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  if (parts.length === 2) {
    formatted = parts.join(',');
  } else {
    formatted = parts[0];
  }

  return include_currency ? `$ ${formatted}` : formatted;
}

/**
 * Month names in Spanish
 * @private
 */
const MONTH_NAMES_ES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
];

/**
 * Short month names in Spanish
 * @private
 */
const MONTH_NAMES_SHORT_ES = [
  'ene', 'feb', 'mar', 'abr', 'may', 'jun',
  'jul', 'ago', 'sep', 'oct', 'nov', 'dic'
];

/**
 * Format date in Spanish (Chilean locale)
 * @param {string|Date} date - Date to format
 * @param {Object} [options] - Formatting options
 * @param {string} [options.format='long'] - Format style: 'long', 'short', 'numeric'
 * @returns {string} Formatted date
 *
 * @example
 * formatDate("2025-01-15")
 * // Returns: "15 de enero de 2025"
 *
 * formatDate("2025-01-15", { format: 'short' })
 * // Returns: "15 ene 2025"
 *
 * formatDate("2025-01-15", { format: 'numeric' })
 * // Returns: "15/01/2025"
 */
export function formatDate(date, options = {}) {
  const { format = 'long' } = options;

  if (!date) {
    return '-';
  }

  let date_object;

  if (typeof date === 'string') {
    // Handle ISO date strings (2025-01-15 or 2025-01-15T10:30:00)
    date_object = new Date(date);
  } else if (date instanceof Date) {
    date_object = date;
  } else {
    return '-';
  }

  if (isNaN(date_object.getTime())) {
    return '-';
  }

  const day = date_object.getDate();
  const month = date_object.getMonth();
  const year = date_object.getFullYear();

  switch (format) {
    case 'long':
      return `${day} de ${MONTH_NAMES_ES[month]} de ${year}`;

    case 'short':
      return `${day} ${MONTH_NAMES_SHORT_ES[month]} ${year}`;

    case 'numeric':
      const month_padded = String(month + 1).padStart(2, '0');
      const day_padded = String(day).padStart(2, '0');
      return `${day_padded}/${month_padded}/${year}`;

    default:
      return `${day} de ${MONTH_NAMES_ES[month]} de ${year}`;
  }
}

/**
 * Format date and time in Spanish
 * @param {string|Date} date - Date to format
 * @param {Object} [options] - Formatting options
 * @param {string} [options.format='long'] - Date format style
 * @param {boolean} [options.include_seconds=false] - Include seconds
 * @returns {string} Formatted date and time
 *
 * @example
 * formatDateTime("2025-01-15T14:30:00")
 * // Returns: "15 de enero de 2025, 14:30"
 *
 * formatDateTime("2025-01-15T14:30:45", { include_seconds: true })
 * // Returns: "15 de enero de 2025, 14:30:45"
 */
export function formatDateTime(date, options = {}) {
  const { format = 'long', include_seconds = false } = options;

  if (!date) {
    return '-';
  }

  let date_object;

  if (typeof date === 'string') {
    date_object = new Date(date);
  } else if (date instanceof Date) {
    date_object = date;
  } else {
    return '-';
  }

  if (isNaN(date_object.getTime())) {
    return '-';
  }

  const date_part = formatDate(date_object, { format });

  const hours = String(date_object.getHours()).padStart(2, '0');
  const minutes = String(date_object.getMinutes()).padStart(2, '0');

  let time_part = `${hours}:${minutes}`;

  if (include_seconds) {
    const seconds = String(date_object.getSeconds()).padStart(2, '0');
    time_part += `:${seconds}`;
  }

  return `${date_part}, ${time_part}`;
}

/**
 * Format phone number in Chilean format
 * @param {string} phone - Phone number to format
 * @returns {string} Formatted phone number
 *
 * @example
 * formatPhone("+56912345678")
 * // Returns: "+56 9 1234 5678"
 *
 * formatPhone("912345678")
 * // Returns: "+56 9 1234 5678"
 */
export function formatPhone(phone) {
  if (!phone || typeof phone !== 'string') {
    return '-';
  }

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');

  // Normalize to include country code
  let normalized;

  if (digits.startsWith('56')) {
    normalized = digits;
  } else if (digits.startsWith('9')) {
    normalized = '56' + digits;
  } else {
    return phone; // Return as-is if format is unknown
  }

  // Format: +56 9 XXXX XXXX
  if (normalized.length === 11 && normalized.startsWith('569')) {
    return `+56 ${normalized.slice(2, 3)} ${normalized.slice(3, 7)} ${normalized.slice(7)}`;
  }

  return phone; // Return as-is if length doesn't match
}

/**
 * Format dimensions with units
 * @param {number} width - Width value
 * @param {number} height - Height value
 * @param {string} [unit='cm'] - Unit of measurement
 * @returns {string} Formatted dimensions
 *
 * @example
 * formatDimensions(100, 80)
 * // Returns: "100 x 80 cm"
 *
 * formatDimensions(1.5, 2.0, 'm')
 * // Returns: "1.5 x 2.0 m"
 */
export function formatDimensions(width, height, unit = 'cm') {
  if (width === null || width === undefined || height === null || height === undefined) {
    return '-';
  }

  const width_num = Number(width);
  const height_num = Number(height);

  if (isNaN(width_num) || isNaN(height_num)) {
    return '-';
  }

  return `${width_num} x ${height_num} ${unit}`;
}

/**
 * Format file size in human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 *
 * @example
 * formatFileSize(1024)
 * // Returns: "1 KB"
 *
 * formatFileSize(1536000)
 * // Returns: "1.5 MB"
 */
export function formatFileSize(bytes) {
  if (bytes === null || bytes === undefined || isNaN(bytes)) {
    return '-';
  }

  const number = Number(bytes);

  if (number === 0) return '0 Bytes';

  const units = ['Bytes', 'KB', 'MB', 'GB'];
  const index = Math.floor(Math.log(number) / Math.log(1024));

  if (index >= units.length) {
    return `${(number / Math.pow(1024, units.length - 1)).toFixed(2)} ${units[units.length - 1]}`;
  }

  return `${(number / Math.pow(1024, index)).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

/**
 * Format relative time (e.g., "hace 2 días")
 * @param {string|Date} date - Date to compare
 * @returns {string} Relative time string
 *
 * @example
 * formatRelativeTime(new Date(Date.now() - 3600000))
 * // Returns: "hace 1 hora"
 *
 * formatRelativeTime(new Date(Date.now() - 86400000 * 2))
 * // Returns: "hace 2 días"
 */
export function formatRelativeTime(date) {
  if (!date) {
    return '-';
  }

  let date_object;

  if (typeof date === 'string') {
    date_object = new Date(date);
  } else if (date instanceof Date) {
    date_object = date;
  } else {
    return '-';
  }

  if (isNaN(date_object.getTime())) {
    return '-';
  }

  const now = new Date();
  const diff_ms = now.getTime() - date_object.getTime();

  const seconds = Math.floor(diff_ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (years > 0) {
    return years === 1 ? 'hace 1 año' : `hace ${years} años`;
  }

  if (months > 0) {
    return months === 1 ? 'hace 1 mes' : `hace ${months} meses`;
  }

  if (days > 0) {
    return days === 1 ? 'hace 1 día' : `hace ${days} días`;
  }

  if (hours > 0) {
    return hours === 1 ? 'hace 1 hora' : `hace ${hours} horas`;
  }

  if (minutes > 0) {
    return minutes === 1 ? 'hace 1 minuto' : `hace ${minutes} minutos`;
  }

  return 'hace un momento';
}

/**
 * Canonical avatar widths (2x retina) used across the app. Change here once, take
 * effect everywhere. Each key names the component context, not the display pixel.
 *
 * @example
 * <img src={get_avatar_url(user.profile_image_url, AVATAR_SIZES.nav)} />
 */
export const AVATAR_SIZES = Object.freeze({
  nav: 64,       // Navbar top-right avatar (displays 32px)
  chip: 80,      // Comment rows, small chips (displays 40px)
  hero: 128,     // ArtistProfile hero, MenuPanel mobile (displays 64px)
  menu: 128,     // MenuPanel mobile (w-14/w-16, displays 56-64px)
  menu_xl: 160,  // MenuPanel desktop xl, ProfileSection OAuth (displays 80px)
  card: 256,     // ArtistCard grid (displays 128px)
  profile: 256,  // ProfileHeader (displays 128px)
});

/**
 * Resolve whether Image Transformation should be active.
 *
 * Precedence: localStorage override → VITE_SUPABASE_IMAGE_TRANSFORM build-time flag.
 * Using a function (not a literal env read) prevents Vite dead-code elimination
 * from removing `transform_supabase_url` when env=false, so the localStorage
 * override can still flip behavior in a shipped bundle — useful for smoke testing
 * Supabase Pro activation without a redeploy.
 *
 *   localStorage.setItem('raios_image_transform', 'true')   → force on
 *   localStorage.setItem('raios_image_transform', 'false')  → force off
 *   localStorage.removeItem('raios_image_transform')        → fall back to env
 *
 * @returns {boolean}
 */
function is_transform_enabled() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const ls = window.localStorage.getItem('raios_image_transform');
      if (ls === 'true') return true;
      if (ls === 'false') return false;
    }
  } catch {
    // localStorage may throw in private mode — fall through to env
  }
  return (
    typeof import.meta !== 'undefined' &&
    import.meta.env &&
    import.meta.env.VITE_SUPABASE_IMAGE_TRANSFORM === 'true'
  );
}

/**
 * Transform Supabase Storage URL to use the Image Transformation API for on-demand resizing.
 *
 * Replaces `/storage/v1/object/public/` with `/storage/v1/render/image/public/` and appends
 * width and resize query parameters. Non-Supabase URLs are returned unchanged.
 *
 * Width is coerced to a positive finite number; invalid values (NaN, negative, non-numeric
 * strings) cause the original URL to be returned with a dev-only console warning, so the
 * Supabase Image API never receives a malformed URL that would 400 in production.
 *
 * Preserves URL fragments (`#section`) by splitting before transform and reattaching after.
 *
 * @param {string} url - Original URL (Supabase Storage or any other)
 * @param {number} [width=400] - Target width in pixels
 * @param {string} [resize='cover'] - Resize mode: 'cover' | 'contain' | 'fill'
 * @returns {string} Transformed URL, or original if not a Supabase Storage URL
 *
 * @example
 * // Supabase Storage URL → render URL
 * transform_supabase_url('https://abc.supabase.co/storage/v1/object/public/artwork-images/user/file.jpg', 400)
 * // Returns: 'https://abc.supabase.co/storage/v1/render/image/public/artwork-images/user/file.jpg?width=400&resize=cover'
 *
 * @example
 * // Non-Supabase URL → passthrough unchanged
 * transform_supabase_url('https://picsum.photos/400/600', 400)
 * // Returns: 'https://picsum.photos/400/600'
 */
export function transform_supabase_url(url, width = 400, resize = 'cover') {
  if (!url || typeof url !== 'string') return url;
  if (!url.includes('/storage/v1/object/public/')) return url;

  const width_num = Number(width);
  if (!Number.isFinite(width_num) || width_num <= 0) {
    if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[transform_supabase_url] invalid width, returning original URL:', width);
    }
    return url;
  }

  const rendered = url.replace('/storage/v1/object/public/', '/storage/v1/render/image/public/');
  const hash_idx = rendered.indexOf('#');
  const base = hash_idx >= 0 ? rendered.slice(0, hash_idx) : rendered;
  const fragment = hash_idx >= 0 ? rendered.slice(hash_idx) : '';
  const separator = base.endsWith('?') ? '' : (base.includes('?') ? '&' : '?');
  return `${base}${separator}width=${width_num}&resize=${resize}${fragment}`;
}

/**
 * Get avatar URL with on-demand resizing via Supabase Image Transformation API.
 *
 * Wrapper around `transform_supabase_url` gated by {@link is_transform_enabled}.
 * Apply the component's rendered size at 2x for retina displays — prefer
 * {@link AVATAR_SIZES} constants over magic numbers.
 *
 * Non-Supabase URLs and null/undefined pass through unchanged.
 * When the flag is off, returns the original URL unchanged.
 *
 * @param {string|null|undefined} url - Avatar URL (Supabase Storage or any other)
 * @param {number} [width=64] - Target width in pixels (pass the 2x of the rendered size)
 * @returns {string|null|undefined} Transformed URL (when flag is on) or original
 *
 * @example
 * <img src={get_avatar_url(user.profile_image_url, AVATAR_SIZES.nav)} />
 *
 * @example
 * <img src={get_avatar_url(artist.profile_image_url, AVATAR_SIZES.card)} />
 */
export function get_avatar_url(url, width = 64) {
  if (!is_transform_enabled()) return url;
  return transform_supabase_url(url, width);
}

/**
 * Get artwork thumbnail URL with on-demand resizing via Supabase Image Transformation API.
 *
 * Wrapper around `transform_supabase_url` gated by {@link is_transform_enabled}.
 * Use for grid/list thumbnails of artworks — NOT for detail views with zoom
 * (ArtworkDetail must serve the original for visual quality).
 *
 * Apply the component's rendered size at 2x for retina displays. Non-Supabase URLs
 * and null/undefined pass through unchanged. When the flag is off, returns the
 * original URL unchanged.
 *
 * @param {string|null|undefined} url - Artwork image URL (Supabase Storage or any other)
 * @param {number} [width=400] - Target width in pixels (pass the 2x of the rendered size)
 * @returns {string|null|undefined} Transformed URL (when flag is on) or original
 *
 * @example
 * // Cart mini-thumb rendered 96px → width=192 for retina
 * <img src={get_artwork_thumbnail_url(item.image_url, 192)} />
 *
 * @example
 * // Sales completed thumb rendered 192px → width=384 for retina
 * <img src={get_artwork_thumbnail_url(first_item.image_url, 384)} />
 */
export function get_artwork_thumbnail_url(url, width = 400) {
  if (!is_transform_enabled()) return url;
  return transform_supabase_url(url, width);
}

/**
 * Truncate text with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} max_length - Maximum length
 * @returns {string} Truncated text
 *
 * @example
 * truncateText("This is a very long text", 10)
 * // Returns: "This is a..."
 */
export function truncateText(text, max_length) {
  if (!text || typeof text !== 'string') {
    return '';
  }

  if (text.length <= max_length) {
    return text;
  }

  return text.slice(0, max_length) + '...';
}

/**
 * Format order number for display
 *
 * If order_number is already a readable format (e.g. "ORD-2025-001234"), returns as-is.
 * If order_number is a raw UUID or missing, generates a short readable ID from the order UUID.
 *
 * @param {string|null} order_number - The order_number field from the backend
 * @param {string} order_id - The order UUID (fallback)
 * @returns {string} Formatted order number
 */
export function formatOrderNumber(order_number, order_id) {
  // Already a readable format (e.g. "ORD-2025-001234")
  if (order_number && !isUUID(order_number)) {
    return order_number;
  }

  // Fallback: generate short ID from UUID (first 8 chars uppercase)
  const uuid = order_number || order_id || '';
  const short_id = uuid.replace(/-/g, '').slice(0, 8).toUpperCase();
  return short_id ? `#${short_id}` : '-';
}

/**
 * Check if a string looks like a UUID
 * @private
 */
function isUUID(str) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
}
